import type { MutableRefObject } from 'react'
import { useCallback } from 'react'
import { Platform } from 'react-native'
import Purchases, { PurchasesPackage } from 'react-native-purchases'

import type { RevenueCatInitStatus } from './revenuecat-init-controller'
import type { PurchaseResult } from './useRevenueCatPurchaseResult'
import {
  buildPurchaseProperties,
  buildSubscriptionStartedProperties,
  logBranchEvent,
  MixpanelEvents,
  trackEvent,
} from '../services/analytics'
import { hasActiveAccess } from '../services/entitlement-utils'
import { verifyBackendEntitlement } from '../services/entitlementService'
import { addBreadcrumb, captureException } from '../utils/sentry'
import { purchasePackageOnWeb } from './purchasesWebBilling'
import { resolveWebBillingLocale } from './revenuecat-config'
import { resolvePurchaseOwnershipMismatch } from './revenuecat-ownership'
import {
  getPurchaseErrorDetails,
  isAlreadyPurchasedPurchaseError,
  resolvePurchaseResult,
} from './useRevenueCatPurchaseResult'

type CustomerInfo = Awaited<ReturnType<typeof Purchases.getCustomerInfo>>

interface UseRevenueCatPurchaseOptions {
  userId: string | null
  userEmail: string | null
  userName: string | null
  authUserId: string | null
  revenueCatInitStatus: RevenueCatInitStatus
  isPurchasesConfiguredRef: MutableRefObject<boolean>
  setCustomerInfo: (info: CustomerInfo | undefined) => void
  getAccessToken: () => Promise<string | null>
}

export function useRevenueCatPurchase({
  userId,
  userEmail,
  userName,
  authUserId,
  revenueCatInitStatus,
  isPurchasesConfiguredRef,
  setCustomerInfo,
  getAccessToken,
}: UseRevenueCatPurchaseOptions) {
  const recoverAlreadyPurchasedSubscription =
    useCallback(async (): Promise<PurchaseResult> => {
      try {
        const customerInfo = await Purchases.getCustomerInfo()
        const ownershipMismatch = resolvePurchaseOwnershipMismatch({
          customerInfo,
          revenueCatId: userId,
        })

        if (ownershipMismatch) {
          addBreadcrumb('purchase', 'purchase_duplicate_ownership_mismatch', {
            currentRevenueCatId:
              ownershipMismatch.currentRevenueCatId ?? 'missing',
            originalAppUserId: ownershipMismatch.originalAppUserId,
          })
          setCustomerInfo(customerInfo)
          return ownershipMismatch
        }

        const { hasAccess } = await verifyBackendEntitlement(
          customerInfo,
          authUserId,
          userId,
          getAccessToken,
        )

        setCustomerInfo(customerInfo)

        if (hasAccess) {
          return { outcome: 'success' }
        }
      } catch (recoveryError) {
        console.error(
          'Failed to recover active subscription after duplicate purchase attempt',
          recoveryError,
        )
        captureException(recoveryError, {
          feature: 'purchase',
          tags: { flow: 'recover_already_purchased' },
        })
      }

      return {
        outcome: 'error',
        message: 'This subscription is already active.',
      }
    }, [authUserId, getAccessToken, setCustomerInfo, userId])

  const purchase = useCallback(
    async (pkg: PurchasesPackage): Promise<PurchaseResult> => {
      if (revenueCatInitStatus !== 'ready') {
        console.error('RevenueCat not initialized')
        return { outcome: 'error', message: 'RevenueCat not initialized' }
      }
      if (!isPurchasesConfiguredRef.current) {
        console.warn('Purchase skipped: RevenueCat is not configured')
        return { outcome: 'error', message: 'RevenueCat is not configured' }
      }

      try {
        let customerInfo: CustomerInfo
        if (Platform.OS === 'web') {
          const locale = resolveWebBillingLocale()
          customerInfo = await purchasePackageOnWeb(
            pkg,
            userEmail,
            userName,
            locale,
          )
        } else {
          const currentCustomerInfo = await Purchases.getCustomerInfo()
          const ownershipMismatch = resolvePurchaseOwnershipMismatch({
            customerInfo: currentCustomerInfo,
            revenueCatId: userId,
          })

          if (ownershipMismatch) {
            addBreadcrumb('purchase', 'purchase_precheck_ownership_mismatch', {
              currentRevenueCatId:
                ownershipMismatch.currentRevenueCatId ?? 'missing',
              originalAppUserId: ownershipMismatch.originalAppUserId,
            })
            setCustomerInfo(currentCustomerInfo)
            return ownershipMismatch
          }

          const nativePurchaseResult = await Purchases.purchasePackage(pkg)
          customerInfo = nativePurchaseResult.customerInfo
        }

        setCustomerInfo(customerInfo)
        if (hasActiveAccess(customerInfo)) {
          const store =
            Platform.OS === 'ios'
              ? 'app_store'
              : Platform.OS === 'android'
                ? 'play_store'
                : 'web'
          const isTrial =
            pkg.product.introPrice != null && pkg.product.introPrice.price === 0
          const billingPeriod =
            typeof pkg.product.subscriptionPeriod === 'string' &&
            pkg.product.subscriptionPeriod.length > 0
              ? pkg.product.subscriptionPeriod
              : String(pkg.packageType)

          trackEvent(
            MixpanelEvents.PurchaseCompleted,
            buildPurchaseProperties({
              userId: authUserId,
              productId: pkg.product.identifier,
              store,
              price: pkg.product.price,
              currency: pkg.product.currencyCode,
              planType: String(pkg.packageType),
              isTrial,
            }),
          )

          trackEvent(
            MixpanelEvents.SubscriptionStarted,
            buildSubscriptionStartedProperties({
              plan: String(pkg.packageType),
              price: pkg.product.price,
              currency: pkg.product.currencyCode,
              billingPeriod,
            }),
          )
          logBranchEvent(isTrial ? 'START_TRIAL' : 'PURCHASE', {
            revenue: pkg.product.price,
            currency: pkg.product.currencyCode,
          })
          logBranchEvent('SUBSCRIBE')

          return { outcome: 'success' }
        }

        return {
          outcome: 'error',
          message: 'No active entitlement after purchase',
        }
      } catch (error: unknown) {
        if (isAlreadyPurchasedPurchaseError(error)) {
          addBreadcrumb('purchase', 'purchase_already_purchased')
          return recoverAlreadyPurchasedSubscription()
        }

        const result = resolvePurchaseResult(error)

        if (result.outcome === 'cancelled') {
          addBreadcrumb('purchase', 'user_cancelled_purchase')
        } else if (result.outcome === 'requires_restore') {
          addBreadcrumb('purchase', 'purchase_requires_restore', {
            errorCode: result.errorCode,
          })
        } else {
          const errorDetails = getPurchaseErrorDetails(error)

          console.error('Purchase failed', error)
          addBreadcrumb(
            'purchase',
            'purchase_failed',
            {
              packageId: pkg.product.identifier,
              errorCode: errorDetails.code ?? 'unknown',
              readableErrorCode: errorDetails.readableErrorCode ?? 'unknown',
            },
            'error',
          )
          captureException(error, {
            feature: 'purchase',
            tags: {
              flow: 'purchase_package',
              packageId: pkg.identifier,
              revenueCatErrorCode: errorDetails.code ?? 'unknown',
              revenueCatReadableErrorCode:
                errorDetails.readableErrorCode ?? 'unknown',
            },
            extra: {
              purchaseResultMessage:
                result.outcome === 'error'
                  ? result.message
                  : (errorDetails.message ?? ''),
              revenueCatDomain: errorDetails.domain,
              revenueCatMessage: errorDetails.message,
              revenueCatUnderlyingErrorMessage:
                errorDetails.underlyingErrorMessage,
              revenueCatUserCancelled: errorDetails.userCancelled,
            },
          })
        }

        return result
      }
    },
    [
      authUserId,
      isPurchasesConfiguredRef,
      recoverAlreadyPurchasedSubscription,
      revenueCatInitStatus,
      setCustomerInfo,
      userId,
      userEmail,
      userName,
    ],
  )

  return { purchase }
}
